import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import CheckIcon from '@mui/icons-material/Check';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { CustomerAgreementInput, CustomerAgreementLanguage } from '../types';
import { isValidCustomerAgreementName, readName } from '../customerAgreementName';
import { Translate } from '../../../shared/LanguageService';
import { whiteNotchedOutline } from '../../../shared/whiteNotchedOutline';
import { agreementTextHasContent } from '../agreementTextHasContent';
import { AgreementTextEditor } from './AgreementTextEditor';

type CustomerAgreementDialogProps = {
    open: boolean;
    title: string;
    languages: CustomerAgreementLanguage[];
    preferredLanguage?: string;
    initial: (CustomerAgreementInput & { isActive: boolean }) | null;
    saving?: boolean;
    onSave: (input: CustomerAgreementInput & { isActive?: boolean }) => void;
    onClose: () => void;
};

type ContentByLanguage = { [language: string]: string };

function initialContent(
    languages: CustomerAgreementLanguage[],
    initial: CustomerAgreementInput | null
) {
    return languages.reduce<ContentByLanguage>((content, language) => {
        const text = initial
            ? initial.texts.find((candidate) => candidate.language === language.key)
            : undefined;
        return { ...content, [language.key]: text ? text.content : '' };
    }, {});
}

function CustomerAgreementForm({
    title,
    languages,
    preferredLanguage,
    initial,
    saving,
    onSave,
    onClose
}: Omit<CustomerAgreementDialogProps, 'open'>) {
    const [name, setName] = React.useState(initial ? initial.name : '');
    const [isActive, setIsActive] = React.useState(initial ? initial.isActive : true);
    const [content, setContent] = React.useState<ContentByLanguage>(() =>
        initialContent(languages, initial)
    );

    const [selectedLanguage, setSelectedLanguage] = React.useState(() =>
        languages.some((language) => language.key === preferredLanguage)
            ? preferredLanguage
            : languages.length > 0
            ? languages[0].key
            : ''
    );

    const languagesWithText = languages.filter((language) =>
        agreementTextHasContent(content[language.key])
    );
    const canSave = isValidCustomerAgreementName(name) && languagesWithText.length > 0;

    function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        const input = {
            name: readName(name),
            texts: languagesWithText.map((language) => ({
                language: language.key,
                content: content[language.key]
            }))
        };
        onSave(initial ? { ...input, isActive } : input);
    }

    return (
        <form onSubmit={handleSubmit}>
            <DialogTitle>{Translate(title)}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{ pt: 1, ...whiteNotchedOutline }}>
                    <TextField
                        id="customer-agreement-name"
                        name="name"
                        label={Translate('customer-agreements/modal/field/name')}
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        required
                        fullWidth
                    />
                    <Typography variant="body2">
                        {Translate('customer-agreements/modal/helper/at-least-one-language')}
                    </Typography>
                    <Box>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                columnGap: 2
                            }}
                        >
                            <Tabs
                                value={selectedLanguage}
                                onChange={(event, language: string) =>
                                    setSelectedLanguage(language)
                                }
                                variant="scrollable"
                                sx={{ minWidth: 0 }}
                            >
                                {languages.map((language) => (
                                    <Tab
                                        key={language.key}
                                        id={`customer-agreement-tab-${language.key}`}
                                        aria-controls={`customer-agreement-tab-panel-${language.key}`}
                                        value={language.key}
                                        label={Translate(language.translationKey)}
                                        iconPosition="end"
                                        icon={
                                            agreementTextHasContent(content[language.key]) ? (
                                                <CheckIcon
                                                    fontSize="small"
                                                    titleAccess={Translate(
                                                        'customer-agreements/modal/tab/has-text'
                                                    )}
                                                />
                                            ) : undefined
                                        }
                                    />
                                ))}
                            </Tabs>
                            {initial ? (
                                <FormControlLabel
                                    sx={{ mr: 0 }}
                                    control={
                                        <Checkbox
                                            name="isActive"
                                            checked={isActive}
                                            onChange={(event) => setIsActive(event.target.checked)}
                                        />
                                    }
                                    label={Translate('customer-agreements/modal/field/is-active')}
                                />
                            ) : null}
                        </Box>
                        {languages
                            .filter((language) => language.key === selectedLanguage)
                            .map((language) => (
                                <Box
                                    key={language.key}
                                    role="tabpanel"
                                    id={`customer-agreement-tab-panel-${language.key}`}
                                    aria-labelledby={`customer-agreement-tab-${language.key}`}
                                    sx={{ pt: 2 }}
                                >
                                    <AgreementTextEditor
                                        label={Translate(
                                            'customer-agreements/modal/field/content',
                                            {
                                                language: Translate(language.translationKey)
                                            }
                                        )}
                                        value={content[language.key]}
                                        onChange={(text) =>
                                            setContent((current) => ({
                                                ...current,
                                                [language.key]: text
                                            }))
                                        }
                                    />
                                </Box>
                            ))}
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                {initial ? (
                    <Typography variant="body2" sx={{ mr: 'auto', pl: 2 }}>
                        {Translate('customer-agreements/modal/helper/new-version')}
                    </Typography>
                ) : null}
                <Button type="button" onClick={onClose}>
                    {Translate('customer-agreements/modal/button/cancel')}
                </Button>
                <Button type="submit" variant="contained" disabled={!canSave || saving}>
                    {Translate('customer-agreements/modal/button/save')}
                </Button>
            </DialogActions>
        </form>
    );
}

export function CustomerAgreementDialog({
    open,
    onClose,
    ...formProps
}: CustomerAgreementDialogProps) {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth disableEnforceFocus>
            <CustomerAgreementForm onClose={onClose} {...formProps} />
        </Dialog>
    );
}
